import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./Reveal";
import { scrollToId } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { amount: "£25,000", label: "Early Investor" },
  { amount: "£50,000", label: "Growth Investor" },
  { amount: "£75,000", label: "Strategic Investor" },
];
const AMOUNTS = ["£25,000", "£50,000", "£75,000", "Other"];

const inputCls =
  "w-full rounded-md border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/60";

const Field = ({ label, id, optional, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
      {label}
      {optional && <span className="ml-2 normal-case tracking-normal text-white/50">(optional)</span>}
    </label>
    {children}
  </div>
);

export default function InvestNow() {
  const empty = { name: "", email: "", phone: "", company: "", country: "", amount: "£25,000", message: "", consent: false, website: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const chooseTier = (amount) => {
    setForm((f) => ({ ...f, amount }));
    scrollToId("invest-now-form");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/investment-interest`, form);
      toast.success("Thank you — your investment interest has been registered.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : "Sorry, your submission could not be sent. Please try again or email us directly at info@airobotcamstudio.com."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="invest-now" data-testid="invest-now-section" className="relative py-16 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>Investor Interest</SectionTag>
          <h2 className="font-display mt-5 max-w-3xl text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Register Interest in the Next Generation of <span className="text-cyan-300">AI Entertainment.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#C5CBD5]">
            AIROBOTCAMSTUDIO LTD is preparing to raise early-stage investment to develop and
            commercialise its real-time AI companion platform. Prospective investors may register
            interest in the following indicative investment levels.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {TIERS.map(({ amount, label }, i) => (
            <Reveal key={amount} delay={0.08 * i}>
              <div
                data-testid={`invest-tier-${i}`}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-cyan-300/30 lg:p-10"
              >
                <p className="font-display text-5xl font-extralight tracking-tighter text-white lg:text-6xl">
                  {amount}
                </p>
                <p className="font-display mt-4 text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300">
                  {label}
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/55">
                  Indicative level · Expression of interest only
                </p>
                <button
                  data-testid={`invest-tier-cta-${i}`}
                  onClick={() => chooseTier(amount)}
                  className="mt-8 w-full rounded-md border border-cyan-300/30 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-cyan-200/90 transition-all duration-300 hover:border-cyan-300/60 hover:text-cyan-100"
                >
                  Register Investment Interest
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="invest-disclosure" className="mt-12 rounded-xl border border-white/10 bg-white/[0.03] p-7 lg:p-8">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-cyan-300/90">
              Important Information
            </p>
            <p className="mt-4 text-xs leading-relaxed text-white/50">
              This page is intended to allow prospective investors to register an initial expression
              of interest only. It does not constitute an offer to the public, an investment
              recommendation, financial advice or a binding invitation to subscribe for shares. Any
              investment will be subject to investor eligibility, appropriate legal documentation,
              company approval and applicable UK financial promotion requirements.
              AIROBOTCAMSTUDIO LTD intends to seek qualification under the Seed Enterprise
              Investment Scheme (SEIS). SEIS tax relief is not guaranteed and depends on the company
              and individual investor satisfying HMRC requirements.
            </p>
            <p data-testid="invest-risk-statement" className="mt-4 border-t border-white/10 pt-4 text-xs font-semibold leading-relaxed text-white/70">
              Capital at risk. Investments in early-stage companies are high risk and illiquid.
              Investors may lose some or all of the amount invested.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div id="invest-now-form" data-testid="invest-form-card" className="mx-auto mt-16 max-w-2xl rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10">
            <SectionTag>Investor Interest</SectionTag>
            <h3 className="font-display mt-4 text-2xl font-light tracking-tighter text-white">
              Register Your <span className="text-cyan-300">Interest</span>
            </h3>
            <form data-testid="invest-form" className="mt-8 space-y-5" onSubmit={submit}>
              <input
                type="text"
                data-testid="invest-honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" id="invn-name">
                  <input id="invn-name" data-testid="invest-name-input" required maxLength={120} className={inputCls} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
                </Field>
                <Field label="Email Address" id="invn-email">
                  <input id="invn-email" data-testid="invest-email-input" required type="email" className={inputCls} value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Phone Number" id="invn-phone" optional>
                  <input id="invn-phone" data-testid="invest-phone-input" type="tel" maxLength={60} className={inputCls} value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 …" />
                </Field>
                <Field label="Company / Organisation" id="invn-company" optional>
                  <input id="invn-company" data-testid="invest-company-input" maxLength={160} className={inputCls} value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Country of Residence" id="invn-country">
                  <input id="invn-country" data-testid="invest-country-input" required maxLength={120} className={inputCls} value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="United Kingdom" />
                </Field>
                <Field label="Intended Investment Amount" id="invn-amount">
                  <select id="invn-amount" data-testid="invest-amount-select" className={inputCls} value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}>
                    {AMOUNTS.map((a) => (
                      <option key={a} value={a} className="bg-black">{a}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Message / Questions" id="invn-message" optional>
                <textarea id="invn-message" data-testid="invest-message-input" rows={4} maxLength={4000} className={inputCls} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything you'd like to ask or tell us" />
              </Field>
              <label data-testid="invest-consent-label" className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 bg-black/30 p-4">
                <input
                  type="checkbox"
                  data-testid="invest-consent-checkbox"
                  required
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-300"
                />
                <span className="text-xs leading-relaxed text-[#C5CBD5]">
                  I understand that submitting this form is an expression of interest only and does
                  not constitute an investment, share subscription or binding commitment.
                </span>
              </label>
              <button
                data-testid="invest-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full rounded-md bg-cyan-300 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Submit Investor Interest"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
